<template>
  <Teleport to="body">
    <Transition name="confirm-fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
        @click.self="handleCancel"
        @keydown.esc="handleCancel"
        tabindex="-1"
      >
        <Transition name="confirm-scale">
          <div
            v-if="isOpen"
            class="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-slate-100 transform transition-all"
            role="dialog"
            aria-modal="true"
          >
            <div class="flex items-start gap-4">
              <!-- Icon -->
              <div
                class="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                :class="{
                  'bg-red-50 text-red-600': type === 'danger',
                  'bg-amber-50 text-amber-600': type === 'warning',
                  'bg-blue-50 text-blue-600': type === 'info',
                }"
              >
                <svg
                  v-if="type === 'danger'"
                  class="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                <svg
                  v-else-if="type === 'warning'"
                  class="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <svg
                  v-else
                  class="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <h3 class="text-lg font-bold text-slate-900 leading-6">
                  {{ title }}
                </h3>
                <p class="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                  {{ message }}
                </p>
              </div>
            </div>

            <!-- Actions -->
            <div class="mt-6 flex justify-end items-center gap-3">
              <button
                type="button"
                @click="handleCancel"
                class="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-colors"
              >
                {{ cancelText }}
              </button>
              <button
                type="button"
                @click="handleConfirm"
                class="px-5 py-2 text-sm font-semibold text-white rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all active:scale-95"
                :class="{
                  'bg-red-600 hover:bg-red-700 focus:ring-red-500': type === 'danger',
                  'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500': type === 'warning',
                  'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500': type === 'info',
                }"
              >
                {{ confirmText }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useConfirmStore } from '@/stores/confirm'

const confirmStore = useConfirmStore()
const { isOpen, title, message, confirmText, cancelText, type } = storeToRefs(confirmStore)

const { handleConfirm, handleCancel } = confirmStore
</script>

<style scoped>
.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.2s ease;
}
.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}

.confirm-scale-enter-active {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
.confirm-scale-leave-active {
  transition: all 0.15s ease-in;
}
.confirm-scale-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}
.confirm-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
